import { supabase } from '@/integrations/supabase/client';

export interface RolloverAuditLog {
  id: string;
  company_id: string;
  rollover_year: number;
  target_year: number;
  operation_type: 'annual_rollover' | 'manual_adjustment' | 'expiration' | 'policy_assignment';
  status: 'in_progress' | 'completed' | 'failed' | 'cancelled';
  initiated_by: string;
  employee_count?: number;
  policies_processed?: number;
  total_balances_updated?: number;
  started_at: string;
  completed_at?: string;
  execution_duration_ms?: number;
  operation_summary?: Record<string, any>;
  error_details?: Record<string, any>;
  rollover_config?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface RolloverBalanceHistory {
  id: string;
  audit_log_id: string;
  profile_id: string;
  policy_id: string;
  company_id: string;
  rollover_year: number;
  target_year: number;
  previous_balance_days?: number;
  previous_carried_over_days?: number;
  previous_annual_entitlement_days?: number;
  new_balance_days?: number;
  new_carried_over_days?: number;
  new_annual_entitlement_days?: number;
  calculated_entitlement?: number;
  tenure_adjustment?: number;
  carryover_applied?: number;
  carryover_expired?: number;
  created_at: string;
}

export interface BalanceExpiration {
  id: string;
  balance_id: string;
  profile_id: string;
  policy_id: string;
  company_id: string;
  carried_over_days: number;
  expiration_date: string;
  expiration_reason: string;
  status: 'scheduled' | 'expired' | 'cancelled';
  processed_at?: string;
  policy_name?: string;
  employee_name?: string;
  created_at: string;
  updated_at: string;
}

export interface RolloverPreview {
  employee_count: number;
  policies_affected: number;
  total_current_balances: number;
  total_carryover: number;
  total_new_entitlements: number;
  estimated_duration_minutes: number;
  preview_details: Array<{
    profile_id: string;
    employee_name: string;
    policy_name: string;
    current_balance: number;
    carryover_amount: number;
    new_entitlement: number;
    final_balance: number;
  }>;
}

const handleAuditError = (error: any, operation: string) => {
  console.error(`[Rollover Audit Service] ${operation} failed:`, error);
  
  if (error.code === 'PGRST116') {
    throw new Error('Audit operation not found');
  }
  if (error.code === '42501') {
    throw new Error('Insufficient permissions for audit operation');
  }
  
  throw new Error(error.message || `Failed to ${operation}`);
};

export const rolloverAuditService = {
  /**
   * Create a new rollover audit log
   */
  async createAuditLog(params: {
    rollover_year: number;
    target_year?: number;
    operation_type: RolloverAuditLog['operation_type'];
    rollover_config?: Record<string, any>;
  }): Promise<RolloverAuditLog> {
    try {
      const target_year = params.target_year || params.rollover_year + 1;
      const user = await supabase.auth.getUser();
      
      // Get user's company_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.data.user?.id)
        .single();
      
      const { data, error } = await supabase
        .from('rollover_audit_logs')
        .insert({
          company_id: profile?.company_id,
          rollover_year: params.rollover_year,
          target_year,
          operation_type: params.operation_type,
          rollover_config: params.rollover_config || {},
          initiated_by: user.data.user?.id
        })
        .select()
        .single();

      if (error) throw error;
      return data as RolloverAuditLog;
    } catch (error) {
      handleAuditError(error, 'create audit log');
      throw error;
    }
  },

  /**
   * Update audit log status and results
   */
  async updateAuditLog(
    auditLogId: string, 
    updates: Partial<Pick<RolloverAuditLog, 'status' | 'employee_count' | 'policies_processed' | 'total_balances_updated' | 'completed_at' | 'execution_duration_ms' | 'operation_summary' | 'error_details'>>
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('rollover_audit_logs')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', auditLogId);

      if (error) throw error;
    } catch (error) {
      handleAuditError(error, 'update audit log');
      throw error;
    }
  },

  /**
   * Record balance history for audit trail
   */
  async recordBalanceHistory(histories: Omit<RolloverBalanceHistory, 'id' | 'created_at'>[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('rollover_balance_history')
        .insert(histories);

      if (error) throw error;
    } catch (error) {
      handleAuditError(error, 'record balance history');
      throw error;
    }
  },

  /**
   * Get audit logs for a company
   */
  async getAuditLogs(filters?: {
    rollover_year?: number;
    operation_type?: RolloverAuditLog['operation_type'];
    status?: RolloverAuditLog['status'];
    limit?: number;
  }): Promise<RolloverAuditLog[]> {
    try {
      let query = supabase
        .from('rollover_audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.rollover_year) {
        query = query.eq('rollover_year', filters.rollover_year);
      }
      if (filters?.operation_type) {
        query = query.eq('operation_type', filters.operation_type);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as RolloverAuditLog[]) || [];
    } catch (error) {
      handleAuditError(error, 'get audit logs');
      throw error;
    }
  },

  /**
   * Get balance history for an audit log
   */
  async getBalanceHistory(auditLogId: string): Promise<RolloverBalanceHistory[]> {
    try {
      const { data, error } = await supabase
        .from('rollover_balance_history')
        .select('*')
        .eq('audit_log_id', auditLogId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleAuditError(error, 'get balance history');
      throw error;
    }
  },

  /**
   * Schedule balance expiration
   */
  async scheduleBalanceExpiration(expirations: Omit<BalanceExpiration, 'id' | 'created_at' | 'updated_at'>[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('balance_expirations')
        .insert(expirations);

      if (error) throw error;
    } catch (error) {
      handleAuditError(error, 'schedule balance expiration');
      throw error;
    }
  },

  /**
   * Get scheduled expirations
   */
  async getScheduledExpirations(filters?: {
    upcoming_days?: number;
    status?: BalanceExpiration['status'];
  }): Promise<BalanceExpiration[]> {
    try {
      let query = supabase
        .from('balance_expirations')
        .select('*')
        .order('expiration_date', { ascending: true });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.upcoming_days) {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + filters.upcoming_days);
        query = query.lte('expiration_date', futureDate.toISOString().split('T')[0]);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as BalanceExpiration[]) || [];
    } catch (error) {
      handleAuditError(error, 'get scheduled expirations');
      throw error;
    }
  },

  /**
   * Process balance expiration
   */
  async processExpiration(expirationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('balance_expirations')
        .update({
          status: 'expired',
          processed_at: new Date().toISOString()
        })
        .eq('id', expirationId);

      if (error) throw error;
    } catch (error) {
      handleAuditError(error, 'process expiration');
      throw error;
    }
  },

  /**
   * Generate rollover preview
   */
  async generateRolloverPreview(rolloverYear?: number): Promise<RolloverPreview> {
    try {
      const year = rolloverYear || new Date().getFullYear() - 1;
      
      // For now, generate a mock preview since the RPC doesn't exist yet
      // In production, you would create the preview_annual_rollover RPC function
      const { data: balances } = await supabase
        .from('leave_balances')
        .select(`
          *,
          profiles:profile_id (first_name, last_name),
          leave_policies:policy_id (name)
        `)
        .eq('balance_year', year);
      
      const preview: RolloverPreview = {
        employee_count: balances?.length || 0,
        policies_affected: new Set(balances?.map(b => (b as any).policy_id) || []).size,
        total_current_balances: balances?.reduce((sum, b) => sum + (b.current_balance_days || 0), 0) || 0,
        total_carryover: balances?.reduce((sum, b) => sum + (b.carried_over_days || 0), 0) || 0,
        total_new_entitlements: balances?.reduce((sum, b) => sum + (b.annual_entitlement_days || 0), 0) || 0,
        estimated_duration_minutes: Math.max(1, Math.ceil((balances?.length || 0) / 10)),
        preview_details: (balances || []).map(b => ({
          profile_id: b.profile_id,
          employee_name: `${(b as any).profiles?.first_name || ''} ${(b as any).profiles?.last_name || ''}`.trim(),
          policy_name: (b as any).leave_policies?.name || '',
          current_balance: b.current_balance_days || 0,
          carryover_amount: b.carried_over_days || 0,
          new_entitlement: b.annual_entitlement_days || 0,
          final_balance: (b.current_balance_days || 0) + (b.annual_entitlement_days || 0)
        }))
      };
      
      return preview;
    } catch (error) {
      handleAuditError(error, 'generate rollover preview');
      throw error;
    }
  },

  /**
   * Execute rollover with full audit trail
   */
  async executeRolloverWithAudit(rolloverYear?: number): Promise<{
    auditLogId: string;
    result: any;
  }> {
    const year = rolloverYear || new Date().getFullYear() - 1;
    let auditLog: RolloverAuditLog | null = null;
    
    try {
      // Create audit log
      auditLog = await this.createAuditLog({
        rollover_year: year,
        operation_type: 'annual_rollover',
        rollover_config: { 
          rollover_year: year,
          automated: false,
          preview_generated: true
        }
      });

      const startTime = Date.now();

      // Execute rollover
      const { data, error } = await supabase.rpc('process_annual_leave_rollover', {
        p_rollover_year: year
      });

      if (error) throw error;

      const executionTime = Date.now() - startTime;
      const result = data as any;

      // Update audit log with success
      await this.updateAuditLog(auditLog.id, {
        status: 'completed',
        employee_count: result.processed_count,
        policies_processed: result.processed_count, // This could be enhanced with actual policy count
        total_balances_updated: result.processed_count,
        completed_at: new Date().toISOString(),
        execution_duration_ms: executionTime,
        operation_summary: {
          rollover_year: result.rollover_year,
          target_year: result.target_year,
          processed_count: result.processed_count,
          company_id: result.company_id
        }
      });

      return {
        auditLogId: auditLog.id,
        result: result
      };
    } catch (error) {
      // Update audit log with failure
      if (auditLog) {
        await this.updateAuditLog(auditLog.id, {
          status: 'failed',
          error_details: {
            message: error instanceof Error ? error.message : 'Unknown error',
            timestamp: new Date().toISOString()
          }
        });
      }
      
      handleAuditError(error, 'execute rollover with audit');
      throw error;
    }
  }
};